import { Box, Stack } from '@mantine/core'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  type BoggleChallengeConfigurationId,
  getBoggleChallengeConfigurationDefinition,
  isBoggleChallengeConfigurationId,
  resolveBoggleChallengeConfigurationRun,
} from '~/boggle/challenges'
import { randomWordGridSeed } from '~/boggle/engine/core'
import { getBoggleGameModeDefinition } from '~/boggle/gameModes'
import { GamePageHeader } from '~/boggle/ui/chrome/AppLayout'
import { ChallengeCatalogScreen } from '~/boggle/ui/challenge/ChallengeCatalogScreen'
import { ChallengeRevealPageHeader } from '~/boggle/ui/challenge/ChallengeRevealPageHeader'
import { ChallengeStagingScreen } from '~/boggle/ui/challenge/ChallengeStagingScreen'
import { getBoggleProgressRepository } from '~/boggle/progress/webProgressRepository'
import { captureBoggleChallengeRevealed } from '~/boggle/telemetry/boggleTelemetry'
import { RunScreen } from '~/boggle/ui/run/RunScreen'
import type { BoggleChallengePhase } from '~/boggle/ui/state/ModeSession'
import { useBoggleModeSession } from '~/boggle/ui/state/ModeSession'

export const Route = createFileRoute('/challenges/$challengeId')({
  head: () => ({
    meta: [
      { title: 'Challenge — Patter' },
      {
        name: 'description',
        content: 'Play a selected challenge',
      },
    ],
  }),
  component: SelectedChallengeRoute,
})

function ChallengeInvalidIdCatalog({
  navigate,
}: {
  navigate: ReturnType<typeof useNavigate>
}) {
  const { clearRouteHostedGameMode } = useBoggleModeSession()

  useLayoutEffect(() => {
    clearRouteHostedGameMode()
  }, [clearRouteHostedGameMode])

  return (
    <Stack gap={0} h="100%" mih={0}>
      <GamePageHeader />
      <Box flex={1} mih={0}>
        <ChallengeCatalogScreen
          onSelect={(configurationId: BoggleChallengeConfigurationId) => {
            void navigate({
              to: '/challenges/$challengeId',
              params: { challengeId: configurationId },
            })
          }}
        />
      </Box>
    </Stack>
  )
}

function SelectedChallengeRoute() {
  const { challengeId } = Route.useParams()
  const navigate = useNavigate()

  if (!isBoggleChallengeConfigurationId(challengeId)) {
    return <ChallengeInvalidIdCatalog navigate={navigate} />
  }

  return <SelectedChallengeFlow challengeId={challengeId} />
}

function SelectedChallengeFlow({
  challengeId,
}: {
  challengeId: Parameters<typeof getBoggleChallengeConfigurationDefinition>[0]
}) {
  const navigate = useNavigate()
  const { clearRouteHostedGameMode } = useBoggleModeSession()
  const [seed] = useState(() => randomWordGridSeed())
  const [phase, setPhase] = useState<BoggleChallengePhase>('reveal')
  const configuration = getBoggleChallengeConfigurationDefinition(challengeId)
  const gameModeId = configuration.gameModeIds[0]!
  const gameMode = getBoggleGameModeDefinition(gameModeId)
  const progressSeed = `${challengeId}:${seed}`
  const resolvedRun = useMemo(
    () => resolveBoggleChallengeConfigurationRun(gameModeId, challengeId, seed),
    [challengeId, gameModeId, seed],
  )
  const [challengeBestScore, setChallengeBestScore] = useState<null | number>(null)
  const revealedTelemetrySignatureRef = useRef<string | null>(null)

  useEffect(() => {
    if (phase !== 'reveal') {
      return
    }

    let cancelled = false
    void getBoggleProgressRepository()
      .getChallengeBestScore(gameModeId, progressSeed)
      .then(bestScore => {
        if (!cancelled) {
          setChallengeBestScore(bestScore)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setChallengeBestScore(null)
        }
      })

    return () => {
      cancelled = true
    }
  }, [gameModeId, phase, progressSeed])

  useEffect(() => {
    if (phase !== 'reveal') {
      return
    }

    const signature = `${gameModeId}:${progressSeed}`
    if (revealedTelemetrySignatureRef.current === signature) {
      return
    }

    revealedTelemetrySignatureRef.current = signature
    captureBoggleChallengeRevealed({
      aceScore: resolvedRun.aceScore,
      challengeConfigurationId: challengeId,
      challengeProgressSeed: progressSeed,
      gameModeId,
      targetScore: resolvedRun.targetScore,
    })
  }, [
    challengeId,
    gameModeId,
    phase,
    progressSeed,
    resolvedRun.aceScore,
    resolvedRun.targetScore,
  ])

  useLayoutEffect(() => {
    clearRouteHostedGameMode()
  }, [clearRouteHostedGameMode])

  let content: ReactNode = (
    <ChallengeStagingScreen
      gameMode={gameMode}
      onBack={() => void navigate({ to: '/challenges' })}
      onPractice={() => setPhase('practice')}
      onStart={() => setPhase('run')}
      resolvedRun={resolvedRun}
      seed={seed}
    />
  )

  if (phase === 'run') {
    content = (
      <RunScreen
        challengeConfigurationId={challengeId}
        challengeProgressSeed={progressSeed}
        gameModeId={gameModeId}
        onBackToChallengeStaging={() => setPhase('reveal')}
        onExitRun={() => setPhase('reveal')}
        onNewGame={() => {}}
        runKind="challenge"
        seed={seed}
      />
    )
  } else if (phase === 'practice') {
    content = (
      <RunScreen
        challengeConfigurationId={challengeId}
        challengeProgressSeed={progressSeed}
        gameModeId={gameModeId}
        onBackToChallengeStaging={() => setPhase('reveal')}
        onExitRun={() => setPhase('reveal')}
        onNewGame={() => {}}
        runKind="challenge-practice"
        seed={seed}
      />
    )
  }

  return (
    <Stack gap={0} h="100%" mih={0}>
      {phase === 'reveal' ? (
        <ChallengeRevealPageHeader
          aceScore={resolvedRun.aceScore}
          bestScore={challengeBestScore}
          targetScore={resolvedRun.targetScore}
        />
      ) : null}
      <Box flex={1} mih={0}>
        {content}
      </Box>
    </Stack>
  )
}
